import React, { useState, useEffect, useCallback } from "react";
import { BarChart3, Package, Users, AlertCircle, FileText, Bell, X } from "lucide-react";
import { db } from "./firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, serverTimestamp, setDoc, query, orderBy, limit } from "firebase/firestore";

const IS_DEV = process.env.NODE_ENV !== "production";
const logDebug = (...args) => {
  if (IS_DEV) console.log(...args);
};
const logWarn = (...args) => {
  if (IS_DEV) console.warn(...args);
};

export default function AdminPage({ user, onLogout, onExit }) {
  const NOTICES_LIMIT = 200;
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [notices, setNotices] = useState([]);
  const [showNoticeForm, setShowNoticeForm] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState("");
  const [noticeContent, setNoticeContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rentals, setRentals] = useState([]);
  const [rentalsLoading, setRentalsLoading] = useState(false);
  const [rentalsSearch, setRentalsSearch] = useState("");
  const [rentalsStatusFilter, setRentalsStatusFilter] = useState("all");
  const [rentalsActionId, setRentalsActionId] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState("");
  const [usersStatusFilter, setUsersStatusFilter] = useState("all");
  const [usersActionId, setUsersActionId] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTab, setLogsTab] = useState("activity");
  const [logsSearch, setLogsSearch] = useState("");
  
  // 대시보드 통계
  const [stats, setStats] = useState({
    totalRentals: 0,
    totalUsers: 0,
    todaySignups: 0,
    totalReports: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  const email = user?.email || "(이메일 없음)";
  const name = user?.displayName || "관리자";

  const menuItems = [
    { id: "dashboard", label: "대시보드", icon: BarChart3 },
    { id: "notice", label: "공지사항", icon: Bell },
    { id: "rentals", label: "매물관리", icon: Package },
    { id: "users", label: "사용자 관리", icon: Users },
    { id: "reports", label: "신고처리", icon: AlertCircle },
    { id: "logs", label: "로그조회", icon: FileText },
  ];

  // 대시보드 통계 조회
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      
      // 총 매물
      let totalRentals = 0;
      let activeRentals = 0;
      let rentedRentals = 0;
      try {
        const rentalsSnapshot = await getDocs(collection(db, "rentals"));
        totalRentals = rentalsSnapshot.size;
        
        const rentalsList = rentalsSnapshot.docs.map(d => ({
          id: d.id,
          title: d.data().title,
          status: d.data().status,
          uid: d.data().uid,
        }));
        
        // 상태별 개수
        activeRentals = rentalsList.filter(r => r.status !== "렌트됨").length;
        rentedRentals = rentalsList.filter(r => r.status === "렌트됨").length;
        
        logDebug("[Admin] rentals count:", totalRentals);
        logDebug("[Admin] rentals - active:", activeRentals, "rented:", rentedRentals);
        logDebug("[Admin] rentals list:", rentalsList);
      } catch (e) {
        console.error("[Admin] rentals 조회 에러:", e);
      }

      // 총 사용자
      let totalUsers = 0;
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        totalUsers = usersSnapshot.size;
        logDebug("[Admin] users count:", totalUsers, "docs:", usersSnapshot.docs.map(d => d.id));
      } catch (e) {
        console.error("[Admin] users 조회 에러:", e);
      }

      // 오늘 가입한 사용자
      let todaySignups = 0;
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        todaySignups = usersSnapshot.docs.filter((doc) => {
          const { createdAt } = doc.data();
          if (!createdAt) return false;
          const userDate = createdAt.seconds
            ? new Date(createdAt.seconds * 1000)
            : new Date(createdAt);
          userDate.setHours(0, 0, 0, 0);
          return userDate.getTime() === today.getTime();
        }).length;
        logDebug("[Admin] todaySignups count:", todaySignups);
      } catch (e) {
        console.error("[Admin] todaySignups 조회 에러:", e);
      }

      // 신고 수
      let totalReports = 0;
      try {
        const reportsSnapshot = await getDocs(collection(db, "reports"));
        totalReports = reportsSnapshot.size;
        logDebug("[Admin] reports count:", totalReports);
      } catch (e) {
        logWarn("[Admin] reports 컬렉션 없음:", e.message);
      }

      setStats({
        totalRentals,
        totalUsers,
        todaySignups,
        totalReports,
      });
      logDebug("[Admin] stats updated:", { totalRentals, totalUsers, todaySignups, totalReports });
    } catch (error) {
      console.error("[Admin] 통계 조회 실패:", error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // 공지사항 목록 조회
  const fetchNotices = useCallback(async () => {
    try {
      const snapshot = await getDocs(
        query(
          collection(db, "notices"),
          orderBy("createdAt", "desc"),
          limit(NOTICES_LIMIT)
        )
      );
      const data = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      setNotices(data);
    } catch (error) {
      console.error("공지사항 조회 실패:", error);
      alert("공지사항 조회 실패: " + error.message);
    }
  }, [NOTICES_LIMIT]);

  // 공지사항 저장
  const handleSaveNotice = async () => {
    if (!noticeTitle.trim() || !noticeContent.trim()) {
      alert("제목과 내용을 입력해주세요.");
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        // 수정
        await updateDoc(doc(db, "notices", editingId), {
          title: noticeTitle,
          content: noticeContent,
          updatedAt: serverTimestamp(),
        });
        alert("공지사항이 수정되었습니다.");
      } else {
        // 신규 작성
        await addDoc(collection(db, "notices"), {
          title: noticeTitle,
          content: noticeContent,
          isPublished: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        alert("공지사항이 작성되었습니다.");
      }
      setNoticeTitle("");
      setNoticeContent("");
      setShowNoticeForm(false);
      setEditingId(null);
      await fetchNotices();
    } catch (error) {
      console.error("공지사항 저장 실패:", error);
      alert("저장 실패: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishNotice = async (notice) => {
    if (!window.confirm("이 공지사항을 배포하시겠습니까?")) return;
    try {
      await updateDoc(doc(db, "notices", notice.id), {
        isPublished: true,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await fetchNotices();
    } catch (error) {
      console.error("공지사항 배포 실패:", error);
      alert("배포 실패: " + error.message);
    }
  };

  // 공지사항 삭제
  const handleDeleteNotice = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await deleteDoc(doc(db, "notices", id));
      alert("공지사항이 삭제되었습니다.");
      await fetchNotices();
    } catch (error) {
      console.error("공지사항 삭제 실패:", error);
      alert("삭제 실패: " + error.message);
    }
  };

  // 공지사항 수정
  const handleEditNotice = (notice) => {
    setEditingId(notice.id);
    setNoticeTitle(notice.title);
    setNoticeContent(notice.content);
    setShowNoticeForm(true);
  };

  // 폼 닫기
  const handleCloseForm = () => {
    setShowNoticeForm(false);
    setEditingId(null);
    setNoticeTitle("");
    setNoticeContent("");
  };

  // 공지사항 날짜 포맷
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString("ko-KR");
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleString("ko-KR");
  };

  const toTimestamp = useCallback((createdAt) => {
    if (!createdAt) return 0;
    if (createdAt.seconds) return createdAt.seconds * 1000;
    if (createdAt.toMillis) return createdAt.toMillis();
    if (createdAt instanceof Date) return createdAt.getTime();
    const parsed = Date.parse(createdAt);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const getUserSortTimestamp = useCallback((userItem) => {
    return Math.max(
      toTimestamp(userItem.updatedAt),
      toTimestamp(userItem.createdAt)
    );
  }, [toTimestamp]);

  const getSortTimestamp = useCallback((rent) => {
    const refreshed = toTimestamp(rent.lastRefreshedAt);
    const created = toTimestamp(rent.createdAt);
    return Math.max(refreshed, created);
  }, [toTimestamp]);

  const fetchRentals = useCallback(async () => {
    try {
      setRentalsLoading(true);
      const snapshot = await getDocs(collection(db, "rentals"));
      const data = snapshot.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
          status: d.data().status || "등록됨",
        }))
        .sort((a, b) => getSortTimestamp(b) - getSortTimestamp(a));
      setRentals(data);
    } catch (error) {
      console.error("[Admin] rentals 조회 실패:", error);
      alert("매물 목록 조회 실패: " + error.message);
    } finally {
      setRentalsLoading(false);
    }
  }, [getSortTimestamp]);

  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const [usersSnapshot, rentalsSnapshot] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "rentals")),
      ]);

      let reportsSnapshot = null;
      try {
        reportsSnapshot = await getDocs(collection(db, "reports"));
      } catch (e) {
        logWarn("[Admin] reports 컬렉션 조회 실패:", e?.message);
      }

      const rentalCounts = new Map();
      rentalsSnapshot.docs.forEach((d) => {
        const uid = d.data()?.uid;
        if (!uid) return;
        rentalCounts.set(uid, (rentalCounts.get(uid) || 0) + 1);
      });

      const reportCounts = new Map();
      if (reportsSnapshot) {
        reportsSnapshot.docs.forEach((d) => {
          const uid = d.data()?.reportedUid || d.data()?.uid;
          if (!uid) return;
          reportCounts.set(uid, (reportCounts.get(uid) || 0) + 1);
        });
      }

      const data = usersSnapshot.docs
        .map((d) => {
          const docData = d.data();
          const uid = docData.uid || d.id;
          return {
            id: d.id,
            uid,
            ...docData,
            status: docData.status || "활성",
            rentalsCount: rentalCounts.get(uid) || 0,
            reportsCount: reportCounts.get(uid) || 0,
          };
        })
        .sort((a, b) => getUserSortTimestamp(b) - getUserSortTimestamp(a));

      const deduped = [];
      const seen = new Set();
      for (const item of data) {
        const emailKey = item.email?.toLowerCase();
        const key = item.uid || item.id || emailKey;
        if (!key) continue;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(item);
      }

      setUsersList(deduped);
    } catch (error) {
      console.error("[Admin] users 조회 실패:", error);
      alert("사용자 목록 조회 실패: " + error.message);
    } finally {
      setUsersLoading(false);
    }
  }, [getUserSortTimestamp]);

  const handleSuspendUser = async (targetUser) => {
    if (targetUser.uid === user?.uid) {
      alert("본인 계정은 정지할 수 없습니다.");
      return;
    }
    const reason = window.prompt("정지 사유를 입력하세요 (선택)", "");
    if (reason === null) return;
    setUsersActionId(targetUser.id);
    try {
      await setDoc(
        doc(db, "users", targetUser.id),
        {
          status: "정지",
          suspendedAt: serverTimestamp(),
          suspendedBy: user?.email || "admin",
          suspendReason: reason || "",
        },
        { merge: true }
      );
      await fetchUsers();
    } catch (error) {
      console.error("[Admin] 사용자 정지 실패:", error);
      alert("사용자 정지 실패: " + error.message);
    } finally {
      setUsersActionId(null);
    }
  };

  const handleUnsuspendUser = async (targetUser) => {
    setUsersActionId(targetUser.id);
    try {
      await setDoc(
        doc(db, "users", targetUser.id),
        {
          status: "활성",
          suspendedAt: null,
          suspendedBy: null,
          suspendReason: null,
        },
        { merge: true }
      );
      await fetchUsers();
    } catch (error) {
      console.error("[Admin] 사용자 해제 실패:", error);
      alert("사용자 해제 실패: " + error.message);
    } finally {
      setUsersActionId(null);
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (targetUser.uid === user?.uid) {
      alert("본인 계정은 삭제할 수 없습니다.");
      return;
    }
    if ((targetUser.status || "활성") !== "정지") {
      alert("정지된 사용자만 삭제할 수 있습니다.");
      return;
    }
    if (!window.confirm("정지된 사용자 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) return;
    setUsersActionId(targetUser.id);
    try {
      await deleteDoc(doc(db, "users", targetUser.id));
      await fetchUsers();
    } catch (error) {
      console.error("[Admin] 사용자 삭제 실패:", error);
      alert("사용자 삭제 실패: " + error.message);
    } finally {
      setUsersActionId(null);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const [activitySnapshot, loginSnapshot] = await Promise.all([
        getDocs(query(collection(db, "activityLogs"), orderBy("createdAt", "desc"))),
        getDocs(query(collection(db, "loginLogs"), orderBy("createdAt", "desc"))),
      ]);

      const activityData = activitySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      const loginData = loginSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setActivityLogs(activityData);
      setLoginLogs(loginData);
    } catch (error) {
      console.error("[Admin] logs 조회 실패:", error);
      alert("로그 조회 실패: " + error.message);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    fetchNotices();
    fetchStats();
  }, [fetchNotices, fetchStats]);

  useEffect(() => {
    if (activeMenu === "rentals") {
      fetchRentals();
    }
  }, [activeMenu, fetchRentals]);

  useEffect(() => {
    if (activeMenu === "users") {
      fetchUsers();
    }
  }, [activeMenu, fetchUsers]);

  useEffect(() => {
    if (activeMenu === "logs") {
      fetchLogs();
    }
  }, [activeMenu, fetchLogs]);

  const normalize = (value) => (value ?? "").toString().toLowerCase();

  const handleForceDeactivate = async (rent) => {
    if (!window.confirm("이 매물을 강제 비활성화하시겠습니까?")) return;
    setRentalsActionId(rent.id);
    try {
      await updateDoc(doc(db, "rentals", rent.id), {
        status: "비활성",
        deactivatedAt: serverTimestamp(),
        deactivatedBy: user?.email || "admin",
      });
      await fetchRentals();
    } catch (error) {
      console.error("[Admin] 강제 비활성화 실패:", error);
      alert("강제 비활성화 실패: " + error.message);
    } finally {
      setRentalsActionId(null);
    }
  };

  const handleActivate = async (rent) => {
    if (!window.confirm("이 매물을 다시 활성화하시겠습니까?")) return;
    setRentalsActionId(rent.id);
    try {
      await updateDoc(doc(db, "rentals", rent.id), {
        status: "등록됨",
        reactivatedAt: serverTimestamp(),
        deactivatedAt: null,
        deactivatedBy: null,
      });
      await fetchRentals();
    } catch (error) {
      console.error("[Admin] 활성화 실패:", error);
      alert("활성화 실패: " + error.message);
    } finally {
      setRentalsActionId(null);
    }
  };

  const handleDeleteRental = async (rent) => {
    if (!window.confirm("비활성화된 매물을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) return;
    setRentalsActionId(rent.id);
    try {
      await deleteDoc(doc(db, "rentals", rent.id));
      await fetchRentals();
    } catch (error) {
      console.error("[Admin] 매물 삭제 실패:", error);
      alert("매물 삭제 실패: " + error.message);
    } finally {
      setRentalsActionId(null);
    }
  };

  const renderContent = () => {
    switch (activeMenu) {
      case "dashboard":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">대시보드</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-slate-600">총 매물</p>
                <p className="mt-2 text-3xl font-bold text-slate-900">
                  {statsLoading ? "-" : stats.totalRentals}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-slate-600">총 사용자</p>
                <p className="mt-2 text-3xl font-bold text-slate-900">
                  {statsLoading ? "-" : stats.totalUsers}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-slate-600">신고</p>
                <p className="mt-2 text-3xl font-bold text-slate-900">
                  {statsLoading ? "-" : stats.totalReports}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-slate-600">오늘 가입</p>
                <p className="mt-2 text-3xl font-bold text-slate-900">
                  {statsLoading ? "-" : stats.todaySignups}
                </p>
              </div>
            </div>
          </div>
        );
      case "notice":
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">공지사항</h2>
              <button
                onClick={() => setShowNoticeForm(true)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                + 공지사항 작성
              </button>
            </div>

            {/* 공지사항 작성 폼 */}
            {showNoticeForm && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">
                    {editingId ? "공지사항 수정" : "공지사항 작성"}
                  </h3>
                  <button
                    onClick={handleCloseForm}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">제목</label>
                    <input
                      type="text"
                      value={noticeTitle}
                      onChange={(e) => setNoticeTitle(e.target.value)}
                      placeholder="공지사항 제목을 입력하세요"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">내용</label>
                    <textarea
                      value={noticeContent}
                      onChange={(e) => setNoticeContent(e.target.value)}
                      placeholder="공지사항 내용을 입력하세요"
                      rows="6"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveNotice}
                      disabled={loading}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-400"
                    >
                      {loading ? "저장 중..." : "저장"}
                    </button>
                    <button
                      onClick={handleCloseForm}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    >
                      취소
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 공지사항 목록 */}
            <div className="space-y-3">
              {notices.length > 0 ? (
                notices.map((notice) => (
                  <div key={notice.id} className="rounded-lg border bg-white p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{notice.title}</h3>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 line-clamp-2">
                          {notice.content}
                        </p>
                        <div className="mt-2 text-xs text-slate-500">
                          {formatDate(notice.createdAt)}
                        </div>
                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                              notice.isPublished
                                ? "bg-green-100 text-green-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {notice.isPublished ? "배포됨" : "대기"}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 flex gap-2">
                        {!notice.isPublished && (
                          <button
                            onClick={() => handlePublishNotice(notice)}
                            className="rounded px-3 py-1 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            배포
                          </button>
                        )}
                        <button
                          onClick={() => handleEditNotice(notice)}
                          className="rounded px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => handleDeleteNotice(notice.id)}
                          className="rounded px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-50"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
                  <p className="text-slate-600">공지사항이 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        );
      case "rentals": {
        const filteredRentals = rentals.filter((rent) => {
          const keyword = normalize(rentalsSearch);
          const matchesKeyword =
            !keyword ||
            [
              rent.title,
              rent.area,
              rent.city,
              rent.category,
              rent.uid,
              rent.status,
            ]
              .map(normalize)
              .some((v) => v.includes(keyword));

          const status = rent.status || "등록됨";
          const matchesStatus =
            rentalsStatusFilter === "all" ||
            (rentalsStatusFilter === "active" && status === "등록됨") ||
            (rentalsStatusFilter === "rented" && status === "렌트됨") ||
            (rentalsStatusFilter === "inactive" && status === "비활성");

          return matchesKeyword && matchesStatus;
        });

        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">매물관리</h2>
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center">
                  <input
                    type="text"
                    value={rentalsSearch}
                    onChange={(e) => setRentalsSearch(e.target.value)}
                    placeholder="제목/지역/도시/카테고리/UID/상태 검색"
                    className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-900 placeholder-slate-500 focus:border-blue-500 focus:outline-none md:max-w-sm"
                  />
                  <select
                    value={rentalsStatusFilter}
                    onChange={(e) => setRentalsStatusFilter(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none md:w-40"
                  >
                    <option value="all">전체</option>
                    <option value="active">등록됨</option>
                    <option value="rented">렌트됨</option>
                    <option value="inactive">비활성</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchRentals}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    disabled={rentalsLoading}
                  >
                    {rentalsLoading ? "불러오는 중..." : "새로고침"}
                  </button>
                  <span className="text-sm text-slate-600">
                    총 {filteredRentals.length}건
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-4 py-3 font-medium">제목</th>
                      <th className="px-4 py-3 font-medium">지역/도시</th>
                      <th className="px-4 py-3 font-medium">카테고리</th>
                      <th className="px-4 py-3 font-medium">가격</th>
                      <th className="px-4 py-3 font-medium">상태</th>
                      <th className="px-4 py-3 font-medium">등록일</th>
                      <th className="px-4 py-3 font-medium">작업</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rentalsLoading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-6 text-center text-slate-500">
                          불러오는 중...
                        </td>
                      </tr>
                    ) : filteredRentals.length > 0 ? (
                      filteredRentals.map((rent) => {
                        const status = rent.status || "등록됨";
                        const isInactive = status === "비활성";
                        const isActioning = rentalsActionId === rent.id;
                        return (
                          <tr key={rent.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{rent.title || "(제목 없음)"}</div>
                              <div className="text-xs text-slate-500">UID: {rent.uid || "-"}</div>
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              {rent.area || "-"} / {rent.city || "-"}
                            </td>
                            <td className="px-4 py-3 text-slate-700">{rent.category || "-"}</td>
                            <td className="px-4 py-3 text-slate-700">{rent.price || "-"}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                                  status === "비활성"
                                    ? "bg-red-100 text-red-700"
                                    : status === "렌트됨"
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{formatDate(rent.createdAt)}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-2">
                                {isInactive ? (
                                  <>
                                    <button
                                      onClick={() => handleActivate(rent)}
                                      disabled={isActioning}
                                      className="rounded-lg border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-60"
                                    >
                                      {isActioning ? "처리 중..." : "활성화"}
                                    </button>
                                    <button
                                      onClick={() => handleDeleteRental(rent)}
                                      disabled={isActioning}
                                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                                    >
                                      {isActioning ? "처리 중..." : "삭제"}
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => handleForceDeactivate(rent)}
                                    disabled={isActioning}
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                                  >
                                    {isActioning ? "처리 중..." : "강제 비활성화"}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-6 text-center text-slate-500">
                          표시할 매물이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      }
      case "users": {
        const filteredUsers = usersList.filter((u) => {
          const keyword = normalize(usersSearch);
          const matchesKeyword =
            !keyword ||
            [u.email, u.displayName, u.uid, u.provider, u.status]
              .map(normalize)
              .some((v) => v.includes(keyword));

          const status = u.status || "활성";
          const matchesStatus =
            usersStatusFilter === "all" ||
            (usersStatusFilter === "active" && status === "활성") ||
            (usersStatusFilter === "suspended" && status === "정지");

          return matchesKeyword && matchesStatus;
        });

        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">사용자 관리</h2>

            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center">
                  <input
                    type="text"
                    value={usersSearch}
                    onChange={(e) => setUsersSearch(e.target.value)}
                    placeholder="이메일/이름/UID/프로바이더/상태 검색"
                    className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-900 placeholder-slate-500 focus:border-blue-500 focus:outline-none md:max-w-sm"
                  />
                  <select
                    value={usersStatusFilter}
                    onChange={(e) => setUsersStatusFilter(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none md:w-40"
                  >
                    <option value="all">전체</option>
                    <option value="active">활성</option>
                    <option value="suspended">정지</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchUsers}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    disabled={usersLoading}
                  >
                    {usersLoading ? "불러오는 중..." : "새로고침"}
                  </button>
                  <span className="text-sm text-slate-600">총 {filteredUsers.length}명</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-4 py-3 font-medium">사용자</th>
                      <th className="px-4 py-3 font-medium">프로바이더</th>
                      <th className="px-4 py-3 font-medium">상태</th>
                      <th className="px-4 py-3 font-medium">매물</th>
                      <th className="px-4 py-3 font-medium">신고</th>
                      <th className="px-4 py-3 font-medium">가입일</th>
                      <th className="px-4 py-3 font-medium">작업</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {usersLoading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-6 text-center text-slate-500">
                          불러오는 중...
                        </td>
                      </tr>
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map((u) => {
                        const status = u.status || "활성";
                        const isSuspended = status === "정지";
                        const isActioning = usersActionId === u.id;
                        const isSelf = u.uid === user?.uid;
                        return (
                          <tr key={u.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{u.displayName || "(이름 없음)"}</div>
                              <div className="text-xs text-slate-500">{u.email || "(이메일 없음)"}</div>
                              <div className="text-xs text-slate-400">UID: {u.uid || "-"}</div>
                            </td>
                            <td className="px-4 py-3 text-slate-700">{u.provider || "-"}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                                  isSuspended
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {status}
                              </span>
                              {isSuspended && u.suspendReason ? (
                                <div className="mt-1 text-xs text-slate-500">사유: {u.suspendReason}</div>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-slate-700">{u.rentalsCount}</td>
                            <td className="px-4 py-3 text-slate-700">{u.reportsCount}</td>
                            <td className="px-4 py-3 text-slate-600">{formatDate(u.createdAt)}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-2">
                                {isSuspended ? (
                                  <>
                                    <button
                                      onClick={() => handleUnsuspendUser(u)}
                                      disabled={isActioning}
                                      className="rounded-lg border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-60"
                                    >
                                      {isActioning ? "처리 중..." : "정지 해제"}
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUser(u)}
                                      disabled={isActioning || isSelf}
                                      className="rounded-lg border border-red-300 bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                                    >
                                      삭제
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => handleSuspendUser(u)}
                                    disabled={isActioning || isSelf}
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                                  >
                                    {isActioning ? "처리 중..." : "정지"}
                                  </button>
                                )}
                                {isSelf && (
                                  <span className="text-xs text-slate-400">본인</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-6 text-center text-slate-500">
                          표시할 사용자가 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      }
      case "reports":
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">신고처리</h2>
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <p className="text-slate-600">신고 목록 및 처리 기능을 여기에 추가하세요.</p>
            </div>
          </div>
        );
      case "logs": {
        const keyword = normalize(logsSearch);
        const filteredActivity = activityLogs.filter((log) => {
          if (!keyword) return true;
          const fields = [
            log.action,
            log.email,
            log.uid,
            log.rentId,
            log.rentTitle,
            log.message,
          ].map(normalize);
          return fields.some((v) => v.includes(keyword));
        });

        const filteredLogin = loginLogs.filter((log) => {
          if (!keyword) return true;
          const fields = [
            log.action,
            log.provider,
            log.email,
            log.uid,
            log.result,
          ].map(normalize);
          return fields.some((v) => v.includes(keyword));
        });

        const activeList = logsTab === "activity" ? filteredActivity : filteredLogin;

        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">로그조회</h2>

            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center">
                  <input
                    type="text"
                    value={logsSearch}
                    onChange={(e) => setLogsSearch(e.target.value)}
                    placeholder="이메일/UID/액션/매물ID 검색"
                    className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-900 placeholder-slate-500 focus:border-blue-500 focus:outline-none md:max-w-sm"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setLogsTab("activity")}
                      className={`rounded-lg px-4 py-2 text-sm font-medium ${
                        logsTab === "activity"
                          ? "bg-blue-600 text-white"
                          : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      활동 로그
                    </button>
                    <button
                      onClick={() => setLogsTab("login")}
                      className={`rounded-lg px-4 py-2 text-sm font-medium ${
                        logsTab === "login"
                          ? "bg-blue-600 text-white"
                          : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      로그인 기록
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchLogs}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    disabled={logsLoading}
                  >
                    {logsLoading ? "불러오는 중..." : "새로고침"}
                  </button>
                  <span className="text-sm text-slate-600">총 {activeList.length}건</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-4 py-3 font-medium">시간</th>
                      <th className="px-4 py-3 font-medium">사용자</th>
                      <th className="px-4 py-3 font-medium">액션</th>
                      <th className="px-4 py-3 font-medium">상세</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logsLoading ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-6 text-center text-slate-500">
                          불러오는 중...
                        </td>
                      </tr>
                    ) : activeList.length > 0 ? (
                      activeList.map((log) => (
                        <tr key={log.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-600">{formatDateTime(log.createdAt)}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{log.email || "(이메일 없음)"}</div>
                            <div className="text-xs text-slate-500">UID: {log.uid || "-"}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{log.action || "-"}</td>
                          <td className="px-4 py-3 text-slate-600">
                            {logsTab === "activity" ? (
                              <div className="space-y-1">
                                {log.rentTitle && <div>매물: {log.rentTitle}</div>}
                                {log.rentId && <div>ID: {log.rentId}</div>}
                                {log.message && <div>{log.message}</div>}
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {log.provider && <div>provider: {log.provider}</div>}
                                {log.result && <div>result: {log.result}</div>}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-4 py-6 text-center text-slate-500">
                          표시할 로그가 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div>
            <p className="text-sm text-slate-500">Admin Console</p>
            <h1 className="text-2xl font-bold">KoreanHome 관리</h1>
            <p className="text-sm text-slate-600 mt-1">{name} · {email}</p>
            <p className="text-xs text-slate-500">UID: {user?.uid || "-"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onExit}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              렌트 리스트로 이동
            </button>
            <button
              onClick={onLogout}
              className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-red-700"
            >
              로그아웃
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-8">
        {/* 사이드바 메뉴 */}
        <aside className="w-48 shrink-0">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveMenu(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeMenu === item.id
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  <Icon size={18} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* 메인 콘텐츠 */}
        <main className="flex-1">
          <div className="rounded-lg border bg-white p-8 shadow-sm">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
}

